import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import Mailcheck from "mailcheck";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    LabelSmall,
    type Typography,
} from "@khanacademy/wonder-blocks-typography";

import {isEmailValid} from "./password-util.ts";

interface Props {
    /**
     * Provide a label for the field.
     */
    label: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Provide a description for the field.
     */
    description?: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Called when the value has changed.
     */
    onChange: (newValue: string) => unknown;
    /**
     * Called when the field input is validated.
     */
    onValidate?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * The initial input value.
     */
    initialValue?: string;
    /**
     * Provide a placeholder for the input field.
     */
    placeholder?: string;
    /**
     * Provide a different empty error message than the default.
     */
    emptyMessage?: string;
    /**
     * Disable the input field.
     */
    disabled?: boolean;
    /**
     * Optional test id for e2e testing.
     * The testId for the internal input element will
     * have "-field" automatically appended.
     */
    testId?: string;
    /**
     * An optional unique identifier for the TextField.
     * If no id is specified, a unique id will be auto-generated.
     */
    id?: string;
    /**
     * Whether the field is required.
     */
    required?: boolean;
}

const DEFAULT_PROPS = {
    initialValue: "",
    placeholder: "",
    emptyMessage: "",
    disabled: false,
} as const;

export const EmailTextField = React.forwardRef<HTMLInputElement, Props>(
    (props, ref) => {
        const {
            label,
            description,
            placeholder = DEFAULT_PROPS.placeholder,
            disabled = DEFAULT_PROPS.disabled,
            testId,
            id,
            required,
            onChange,
            onValidate,
            initialValue = DEFAULT_PROPS.initialValue,
            emptyMessage = DEFAULT_PROPS.emptyMessage,
        } = props;

        const [value, setValue] = React.useState(initialValue);
        const [suggestion, setSuggestion] = React.useState<
            string | null | undefined
        >(null);
        const [hasError, setHasError] = React.useState(false);
        const [errorMessage, setErrorMessage] = React.useState<
            string | null | undefined
        >(null);

        const handleEmailSuggestion = React.useCallback(
            (suggestion?: {full: string} | null | undefined) => {
                if (suggestion?.full) {
                    setSuggestion(suggestion.full);
                }
            },
            [],
        );

        const applyEmailSuggestion = React.useCallback(() => {
            if (suggestion) {
                setValue(suggestion);
                setSuggestion(null);
                onChange(suggestion);
            }
        }, [suggestion, onChange]);

        const handleChange = React.useCallback(
            (newValue: string) => {
                setValue(newValue);
                onChange(newValue);
            },
            [onChange],
        );

        const handleBlur = React.useCallback(() => {
            if (value && isEmailValid(value)) {
                Mailcheck.run({
                    email: value,
                    suggested: handleEmailSuggestion,
                    empty: handleEmailSuggestion,
                });
            }
        }, [value, handleEmailSuggestion]);

        const validate = React.useCallback(
            (value: string) => {
                if (!value) {
                    return emptyMessage || t`Please tell us your email.`;
                }
                if (!isEmailValid(value)) {
                    return t`Please enter a valid email format like example@email.com`;
                }
                return null;
            },
            [emptyMessage],
        );

        const handleValidate = React.useCallback(
            (errorMessage?: string | null | undefined) => {
                setHasError(!!errorMessage);
                setErrorMessage(errorMessage);
                if (onValidate) {
                    onValidate(errorMessage);
                }
            },
            [onValidate],
        );

        const maybeRenderEmailSuggestion = React.useCallback(() => {
            if (!suggestion) {
                return "";
            }
            return (
                <LabelSmall style={styles.description}>
                    <Trans>
                        Did you mean{" "}
                        <Button
                            style={styles.emailSuggestion}
                            size="small"
                            kind="tertiary"
                            onClick={applyEmailSuggestion}
                        >
                            {suggestion}
                        </Button>
                        ?
                    </Trans>
                </LabelSmall>
            );
        }, [suggestion, applyEmailSuggestion]);

        return (
            <React.Fragment>
                <LabeledField
                    id={id}
                    testId={testId}
                    contextLabel={required ? t`required` : undefined}
                    errorMessage={errorMessage}
                    field={
                        <TextField
                            type="email"
                            value={value}
                            placeholder={placeholder}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validate={validate}
                            onValidate={handleValidate}
                            disabled={disabled}
                            ref={ref}
                            required={required}
                        />
                    }
                    label={label}
                    description={description}
                />
                {!hasError && maybeRenderEmailSuggestion()}
            </React.Fragment>
        );
    },
);

EmailTextField.displayName = "EmailTextField";

const styles = StyleSheet.create({
    description: {
        marginBlockStart: sizing.size_120,
    },
    emailSuggestion: {
        height: "auto",
    },
});
